import i18n from "i18n";
import * as Yup from "yup";

import { ProductType } from "constants/hst-constants";
import type { SelectedProduct } from "../../../../../StudentLicense/subpages/CreateInviteForm/components/AddProduct/AddProduct";

export interface NewEmailInviteBundleFormFields {
  // Step 1: Student selection
  studentEmails: string[];

  // Step 2: Product selection
  selectedProducts: SelectedProduct[];
  licenseExpirationDate: Date;

  // Step 3: Group selection
  groupAssignmentMethod: string;
  groupId?: number;
  newGroupName?: string;
}

export const buildNewEmailInviteBundleFormSchema =
  (): Yup.SchemaOf<NewEmailInviteBundleFormFields> =>
    Yup.object({
      studentEmails: Yup.array()
        .of(Yup.string().email())
        .default([])
        .min(
          1,
          i18n.t(
            "modal:createEmailInviteBundleModal.steps.addStudents.fields.studentEmails.errors.min",
          ),
        )
        .required(
          i18n.t(
            "modal:createEmailInviteBundleModal.steps.addStudents.fields.studentEmails.errors.required",
          ),
        ),
      selectedProducts: Yup.array()
        .of(
          Yup.object({
            id: Yup.number().required(),
            name: Yup.string().required(),
            type: Yup.string().optional(),
            initialSkillLevel: Yup.number().when("type", {
              is: ProductType.BUSINESS,
              // biome-ignore lint/suspicious/noThenProperty: This "then" keyword is valid "Yup" syntax, cannot be avoided
              then: (schema) =>
                schema.required(
                  i18n.t(
                    "modal:createEmailInviteBundleModal.steps.groupManagement.fields.productSelection.fields.initialSkillLevel.errors.required",
                  ),
                ),
              otherwise: (schema) => schema.optional(),
            }),
          }),
        )
        .default([])
        .min(
          1,
          i18n.t(
            "modal:createEmailInviteBundleModal.steps.productSelection.fields.selectedProducts.errors.min",
          ),
        )
        .required(
          i18n.t(
            "modal:createEmailInviteBundleModal.steps.productSelection.fields.selectedProducts.errors.required",
          ),
        ),
      licenseExpirationDate: Yup.date()
        .required(
          i18n.t(
            "modal:createEmailInviteBundleModal.steps.productSelection.fields.licenseExpirationDate.errors.required",
          ),
        )
        .min(
          new Date(),
          i18n.t(
            "modal:createEmailInviteBundleModal.steps.productSelection.fields.licenseExpirationDate.errors.min",
          ),
        ),
      groupAssignmentMethod: Yup.string().oneOf(["existing", "new", "none"]).required(),
      groupId: Yup.number().when("groupAssignmentMethod", {
        is: "existing",
        // biome-ignore lint/suspicious/noThenProperty: This "then" keyword is valid "Yup" syntax, cannot be avoided
        then: (schema) =>
          schema.required(
            i18n.t(
              "modal:createEmailInviteBundleModal.steps.groupManagement.fields.groupId.errors.required",
            ),
          ),
        otherwise: (schema) => schema.optional(),
      }),
      newGroupName: Yup.string().when("groupAssignmentMethod", {
        is: "new",
        // biome-ignore lint/suspicious/noThenProperty: This "then" keyword is valid "Yup" syntax, cannot be avoided
        then: (schema) =>
          schema.required(
            i18n.t(
              "modal:createEmailInviteBundleModal.steps.groupManagement.fields.newGroupName.errors.required",
            ),
          ),
        otherwise: (schema) => schema.optional(),
      }),
    }).required();
